import { useCallback, useEffect, useMemo, useState, type ChangeEvent } from 'react'
import { showError } from '@/lib/utils/notifications'
import type { OrganizationLogoFilePick } from '../services/organizationsService'
import type {
  OrganizationDefaultFormDownloadKey,
  OrganizationDefaultFormFileKey,
  OrganizationEmailSignatureView,
  OrganizationLogoKey,
  OrgEditTab,
  UseOrganizationEditAssetsOptions,
} from '../types'
import {
  ALLOWED_LOGO_MIME,
  DEFAULT_FORM_PDF_ACCEPT,
  EMPTY_DEFAULT_FORM_SERVER,
  EMPTY_LOGO_SERVER,
  LOGO_IMAGE_ACCEPT,
} from '../constants/editPage'
import { pickLogoPreviewSrc } from '../utils/logoPreview'
import {
  openOrganizationDefaultFormDownload,
  type OrganizationDefaultFormServeType,
} from '../utils/downloadOrganizationLogo'

export function useOrganizationEditAssets(orgIdOrOpts: UseOrganizationEditAssetsOptions) {
  const orgId = typeof orgIdOrOpts === 'object' ? orgIdOrOpts?.orgId : orgIdOrOpts
  const isEdit = typeof orgIdOrOpts === 'object' ? Boolean(orgIdOrOpts?.isEdit) : Boolean(orgId)
  const onAutoUpload = typeof orgIdOrOpts === 'object' ? orgIdOrOpts?.onAutoUpload : undefined

  const [selectedTab, setSelectedTab] = useState<OrgEditTab>('orgprofile')
  const [logoFiles, setLogoFiles] = useState<Partial<Record<OrganizationLogoKey, File>>>({})
  const [logoServerSrc, setLogoServerSrc] = useState<Record<OrganizationLogoKey, string>>(EMPTY_LOGO_SERVER)
  const [logoServerLine, setLogoServerLine] = useState<Record<OrganizationLogoKey, string>>(EMPTY_LOGO_SERVER)
  const [defaultFormFiles, setDefaultFormFiles] = useState<
    Partial<Record<OrganizationDefaultFormFileKey, File>>
  >({})
  const [defaultFormServerLine, setDefaultFormServerLine] =
    useState<Record<OrganizationDefaultFormFileKey, string>>(EMPTY_DEFAULT_FORM_SERVER)
  const [emailSignatureView, setOrganizationEmailSignatureView] =
    useState<OrganizationEmailSignatureView>('body')
  const [largeLogoConfirm, setLargeLogoConfirm] = useState<
    | { kind: 'logo'; key: OrganizationLogoKey; file: File }
    | { kind: 'defaultForm'; key: OrganizationDefaultFormFileKey; file: File }
    | null
  >(null)
  const [primaryLogoRequiredError, setPrimaryLogoRequiredError] = useState(false)
  const [defaultFormDownloading, setDefaultFormDownloading] =
    useState<OrganizationDefaultFormDownloadKey | null>(null)

  const applyLogoFile = (key: OrganizationLogoKey, file: File) => {
    setLogoFiles((f) => ({ ...f, [key]: file }))
    if (key === 'org_logo') setPrimaryLogoRequiredError(false)
    if (isEdit && onAutoUpload) {
      void onAutoUpload({ [key]: file })
    }
  }

  const handleLogoFileChange = (key: OrganizationLogoKey, e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null
    if (!file) {
      setLogoFiles((f) => {
        const n = { ...f }
        delete n[key]
        return n
      })
      return
    }
    if (!ALLOWED_LOGO_MIME.has(file.type)) {
      showError('Only JPG, JPEG & PNG files are allowed.')
      return
    }
    const mb = file.size / 1024 / 1024
    if (mb > 2) {
      showError('File Size Exceeds Allowable Limit of 2 MB.')
      return
    }
    if (mb > 1) {
      setLargeLogoConfirm({ kind: 'logo', key, file })
      return
    }
    applyLogoFile(key, file)
  }

  const logoBlobPreviewUrls = useMemo(() => {
    const out: Partial<Record<OrganizationLogoKey, string>> = {}
    ;(['org_logo', 'org_swoosh_logo', 'org_image'] as const).forEach((k) => {
      const f = logoFiles[k]
      if (f) out[k] = URL.createObjectURL(f)
    })
    return out
  }, [logoFiles])

  useEffect(() => {
    const urls = logoBlobPreviewUrls
    return () => {
      Object.values(urls).forEach((u) => URL.revokeObjectURL(u))
    }
  }, [logoBlobPreviewUrls])

  const displayLogoLine = (key: OrganizationLogoKey): string => {
    const f = logoFiles[key]
    if (f) return f.name
    return logoServerLine[key] ?? ''
  }

  const logoPreviewSrc = (key: OrganizationLogoKey) =>
    pickLogoPreviewSrc(logoBlobPreviewUrls[key], logoServerSrc[key])

  const onDefaultFormFilePick = (key: OrganizationDefaultFormFileKey, file: File | null) => {
    if (!file) {
      setDefaultFormFiles((f) => {
        const n = { ...f }
        delete n[key]
        return n
      })
      return
    }
    const mb = file.size / 1024 / 1024
    if (mb > 2) {
      showError('File Size Exceeds Allowable Limit of 2 MB.')
      return
    }
    if (mb > 1) {
      setLargeLogoConfirm({ kind: 'defaultForm', key, file })
      return
    }
    setDefaultFormFiles((f) => ({ ...f, [key]: file }))
    if (isEdit && onAutoUpload) {
      void onAutoUpload({ [key]: file })
    }
  }

  const displayDefaultFormFileLine = (key: OrganizationDefaultFormFileKey): string => {
    const f = defaultFormFiles[key]
    if (f) return f.name
    return defaultFormServerLine[key] ?? ''
  }

  const runDefaultFormDownload = useCallback(
    async (which: OrganizationDefaultFormDownloadKey, fileType: OrganizationDefaultFormServeType, path: string) => {
      if (!orgId) return
      setDefaultFormDownloading(which)
      try {
        await openOrganizationDefaultFormDownload(orgId, path, fileType)
      } finally {
        setDefaultFormDownloading(null)
      }
    },
    [orgId],
  )

  const hasAnyPendingFileUpload = useMemo(
    () =>
      Boolean(
        logoFiles.org_logo ||
          logoFiles.org_swoosh_logo ||
          logoFiles.org_image ||
          defaultFormFiles.ach_form_file ||
          defaultFormFiles.statement_instruction_file,
      ),
    [logoFiles, defaultFormFiles],
  )

  const uploadFilePayload = useMemo((): OrganizationLogoFilePick | undefined => {
    if (!hasAnyPendingFileUpload) return undefined
    return {
      org_logo: logoFiles.org_logo,
      org_swoosh_logo: logoFiles.org_swoosh_logo,
      org_image: logoFiles.org_image,
      ach_form_file: defaultFormFiles.ach_form_file,
      statement_instruction_file: defaultFormFiles.statement_instruction_file,
    }
  }, [hasAnyPendingFileUpload, logoFiles, defaultFormFiles])

  const confirmLargeLogoUpload = () => {
    const pending = largeLogoConfirm
    setLargeLogoConfirm(null)
    if (pending) {
      if (pending.kind === 'defaultForm') {
        setDefaultFormFiles((f) => ({ ...f, [pending.key]: pending.file }))
        if (isEdit && onAutoUpload) {
          void onAutoUpload({ [pending.key]: pending.file })
        }
      } else {
        applyLogoFile(pending.key, pending.file)
      }
    }
  }

  return {
    selectedTab,
    setSelectedTab,
    logoFiles,
    setLogoFiles,
    logoServerSrc,
    setLogoServerSrc,
    logoServerLine,
    setLogoServerLine,
    defaultFormFiles,
    setDefaultFormFiles,
    defaultFormServerLine,
    setDefaultFormServerLine,
    emailSignatureView,
    setOrganizationEmailSignatureView,
    largeLogoConfirm,
    setLargeLogoConfirm,
    primaryLogoRequiredError,
    setPrimaryLogoRequiredError,
    defaultFormDownloading,
    handleLogoFileChange,
    displayLogoLine,
    logoPreviewSrc,
    logoBlobPreviewUrls,
    onDefaultFormFilePick,
    displayDefaultFormFileLine,
    runDefaultFormDownload,
    uploadFilePayload,
    confirmLargeLogoUpload,
    logoImageAccept: LOGO_IMAGE_ACCEPT,
    defaultFormPdfAccept: DEFAULT_FORM_PDF_ACCEPT,
  }
}

export type OrganizationEditAssets = ReturnType<typeof useOrganizationEditAssets>
